import {
  sumEffectiveEntries,
  sumEffectiveSettlement,
  type CashFlowEntry as CoreEntry,
  type MoneySum,
  type SettlementTotals,
} from '@progear/finance-core';
import type { CashFlowEntry } from '@/lib/cashFlow';

/**
 * The register scope header summary — the "expected vs actual" breakdown the
 * calendar-day overlay used to show, now reachable from the universal register's
 * header for ANY scope (a day, a line, a counterparty, overdue, or all documents).
 *
 * It is a PURE finance-core fold over the documents currently in scope: incoming
 * and outgoing are summed separately (`sumEffectiveEntries`) so we never invent a
 * signed income-minus-cost number AND a final invoice funded by a received
 * prepayment is netted out (an ETT↔ARV pair is never counted twice), and the
 * settlement split comes from `sumEffectiveSettlement` (the ONE prepayment-netted
 * settlement aggregator) — `paid` is the actual cash that moved, `outstanding` is
 * what is still expected, both on the SAME netted basis as the register totals.
 * Filtering by `kind` is non-money logic (a partition, not arithmetic), so this
 * respects L9 / the master rule: the component renders these figures but never adds
 * money itself.
 */
export interface LedgerScopeSummary {
  /** Number of documents in scope. */
  count: number;
  /** Sum of the incoming documents (Money parts, integer cents). */
  income: MoneySum;
  /** Sum of the outgoing documents (Money parts, integer cents). */
  cost: MoneySum;
  /** Settlement across all documents in scope: paid (actual) vs outstanding (expected). */
  settlement: SettlementTotals;
}

/** Summarise the documents in scope for the register header (pure; finance-core). */
export function summarizeLedgerScope(entries: CashFlowEntry[]): LedgerScopeSummary {
  const income = entries.filter((e) => e.kind === 'income');
  const cost = entries.filter((e) => e.kind === 'cost');
  return {
    count: entries.length,
    income: sumEffectiveEntries(income as CoreEntry[]),
    cost: sumEffectiveEntries(cost as CoreEntry[]),
    settlement: sumEffectiveSettlement(entries),
  };
}
